// This file should never have any imports

import type { Component, Condition, FlowPage } from './types'

type AttrFragments = { [fragment: string]: string[] }

export default class Attrs {
  attrs: AttrFragments
  static_render: boolean
  constructor(staticRender: boolean = false) {
    this.attrs = {}
    this.static_render = staticRender
  }

  add(key: string, value: string | string[]): Attrs {
    const seen = new Set(this.attrs[key] || [])
    Array.isArray(value) ? value.forEach((val) => seen.add(val)) : seen.add(value)
    this.attrs[key] = Array.from(seen)
    return this
  }

  remove(key: string): Attrs {
    delete this.attrs[key]
    return this
  }

  addController(...controllers: string[]): Attrs {
    !this.static_render && this.add('data-controller', controllers)
    return this
  }

  addValue(controller: string, key: string, value: string): Attrs {
    !this.static_render && this.add(`data-${controller}-${key}-value`, value)
    return this
  }

  addValueIfExists(controller: string, key: string, value: string | boolean | undefined): Attrs {
    if (value !== undefined)
      !this.static_render && this.add(`data-${controller}-${key}-value`, `${value}`)
    return this
  }

  addTarget(controllerName: string, targetName: string): Attrs {
    !this.static_render && this.add(`data-${controllerName}-target`, targetName)
    return this
  }

  addAction(...actions: string[]): Attrs {
    !this.static_render && actions.forEach((action) => this.add('data-action', action))
    return this
  }

  addParam(controller: string, key: string, value: string): Attrs {
    !this.static_render && this.add(`data-${controller}-${key}-param`, value)
    return this
  }

  addClass(controller: string, className: string, value: string): Attrs {
    !this.static_render && this.add(`data-${controller}-${className}-class`, value)
    return this
  }

  addDOMClass(...classes: (string | string[])[]): Attrs {
    const toAdd = classes.flat(1)
    this.add('class', toAdd)
    return this
  }

  addUserData(): Attrs {
    !this.static_render && this.add('data-user-data', 'true')
    return this
  }

  addConditions(conditions: Condition[]): Attrs {
    if (conditions.length === 0) return this
    !this.static_render &&
      this.addController('condition').addValue(
        'condition',
        'conditions',
        encodeURIComponent(JSON.stringify(conditions))
      )
    return this
  }

  addIcon(library: string, iconName: string): Attrs {
    !this.static_render
      ? this.addController('icon')
          .addValue('icon', 'library', library)
          .addValue('icon', 'name', iconName)
          .addDOMClass('Icon')
      : this.addDOMClass('Icon')
    return this
  }

  addComponentControllerFragment(component: Component, page: FlowPage): Attrs {
    if (!this.static_render) {
      this.addValue('component', 'type', `${component.type}`)
      this.addValue('component', 'key', component.key)
      this.addValue('component', 'id', component.id)
      this.addValue('component', 'page-id', page.id)
      this.addValue('component', 'page-key', page.key)
    }
    return this
  }

  addReactiveElement(): Attrs {
    return this.add('data-element', 'true')
  }

  toString(): string {
    return Object.entries(this.attrs)
      .map(([key, value]) => `${key}="${value.join(' ')}"`)
      .join(' ')
  }
}
